import { useEffect, useState } from 'react';
import { apiBlob } from '../lib/api';

/**
 * La foto de perfil de una persona, servida detrás de autenticación.
 *
 * ── Por qué no un `<img src="/api/…">` a secas ──
 * El access token vive **en memoria**, no en una cookie, así que un `<img src>` no
 * lo manda: el navegador pediría la imagen sin cabecera `Authorization` y el
 * servidor respondería 401. Se descarga por `fetch` y se muestra como object URL.
 * Es el mismo mecanismo que `LogoCliente`.
 *
 * ⚠️ Los `blob:` los bloquea la CSP de `helmet` cuando Express sirve la SPA —no en
 * el dev server de Vite—. Ya está declarado en `img-src` en `src/app.ts`.
 *
 * ── En qué NO es como el logotipo, y las tres son a propósito ──
 * 1. **`object-cover` y redondo.** Un logotipo recortado deja de ser el logotipo,
 *    pero un retrato se recorta: el servidor ya lo guarda cuadrado con `fit:
 *    'cover'` centrado, y acá se enmarca en círculo, que es lo que la vista espera
 *    de una cara.
 * 2. **El object URL se libera al desmontar, y eso importa más acá.** Sin eso,
 *    recorrer la cola deja en memoria una colección de caras que el recolector no
 *    puede tocar, porque `URL.createObjectURL` las mantiene vivas.
 * 3. **No se guarda en ninguna caché.** La ruta responde `private, no-store`
 *    (D-090): es un dato personal, y una copia en el disco del navegador es una
 *    copia más de la que nadie se acuerda el día que llegue una solicitud de
 *    borrado.
 */
export function Retrato({
  ruta,
  nombre,
  tieneFoto,
  tamano = 'lista',
}: {
  /** Ruta autenticada que devuelve la imagen, p. ej. `/api/solicitudes/7/foto`. */
  ruta: string;
  nombre: string;
  tieneFoto: boolean;
  tamano?: 'lista' | 'ficha';
}) {
  const [url, setUrl] = useState<string | null>(null);
  const [fallo, setFallo] = useState(false);

  useEffect(() => {
    if (!tieneFoto) {
      setUrl(null);
      setFallo(false);
      return;
    }
    let activo = true;
    let creada: string | null = null;

    apiBlob(ruta)
      .then((blob) => {
        if (!activo) return;
        creada = URL.createObjectURL(blob);
        setUrl(creada);
      })
      .catch(() => activo && setFallo(true));

    return () => {
      activo = false;
      if (creada) URL.revokeObjectURL(creada);
    };
  }, [ruta, tieneFoto]);

  const medida = tamano === 'ficha' ? 'h-24 w-24 text-2xl' : 'h-12 w-12 text-sm';
  const marco = `flex shrink-0 items-center justify-center overflow-hidden rounded-full ${medida}`;

  /*
    Sin foto —o si falló al traerla— van las iniciales. Dos y no una: en una lista
    de personas de la misma empresa, una sola letra choca constantemente.
  */
  if (!tieneFoto || fallo) {
    return (
      <span
        aria-hidden="true"
        className={`${marco} bg-marino-50 font-bold tracking-tight text-marino-500`}
      >
        {iniciales(nombre)}
      </span>
    );
  }

  if (!url) return <span className={`${marco} animate-pulse bg-marino-50`} />;

  return (
    <span className={`${marco} border border-marino-100 bg-white`}>
      {/*
        `alt` vacío y `aria-hidden` en el respaldo: el nombre de la persona ya está
        escrito al lado, en texto. Repetirlo en el texto alternativo hace que un
        lector de pantalla lo diga dos veces seguidas.
      */}
      <img src={url} alt="" className="h-full w-full object-cover" />
    </span>
  );
}

/** Primera letra del nombre y del último apellido. */
function iniciales(nombre: string): string {
  const partes = nombre.trim().split(/\s+/).filter(Boolean);
  if (partes.length === 0) return '—';
  const primera = partes[0]!.charAt(0);
  const ultima = partes.length > 1 ? partes[partes.length - 1]!.charAt(0) : '';
  return (primera + ultima).toUpperCase();
}
